import fingerprintJsPro, {
  Agent as ProAgent,
} from "@fingerprintjs/fingerprintjs-pro";

import fingerprintJfFree, {
  Agent as FreeAgent,
} from "@fingerprintjs/fingerprintjs";

import { env } from "config/env";
import { AntiFraudType } from "utils/types/session";

export interface FingerprintResult {
  fingerprintId: string;
  fingerprintType: AntiFraudType;
  fingerprintRequestId?: string;
}

export const FINGERPRINT_TIMEOUT_MS = 10000;

const withTimeout = async <T,>(
  operation: Promise<T>,
  timeoutMs: number
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(`Fingerprint collection timed out after ${timeoutMs}ms`)
      );
    }, timeoutMs);
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getFingerprint = async (
  antiFraudType: AntiFraudType
): Promise<FingerprintResult> => {
  const isProVersion = antiFraudType === AntiFraudType.PRO;
  try {
    if (isProVersion) {
      const collectProFingerprint = async () => {
        const fingerprintJs: ProAgent = await fingerprintJsPro.load({
          apiKey: env.fingerprintJsApiKey,
          scriptUrlPattern: [
            "https://fp-game.myli.io/behavior/agent?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>",
            fingerprintJsPro.defaultScriptUrlPattern, // Fallback to default CDN in case of error
          ],
          endpoint: [
            "https://fp-game.myli.io/behavior/result?region=eu",
            fingerprintJsPro.defaultEndpoint, // Fallback to default endpoint in case of error
          ],
        });
        return fingerprintJs.get();
      };
      const result = await withTimeout(
        collectProFingerprint(),
        FINGERPRINT_TIMEOUT_MS
      );
      return {
        fingerprintId: result.visitorId,
        fingerprintType: AntiFraudType.PRO,
        fingerprintRequestId: result.requestId,
      };
    }

    const collectFreeFingerprint = async () => {
      const fingerprintJs: FreeAgent = await fingerprintJfFree.load();
      return fingerprintJs.get();
    };
    const result = await withTimeout(
      collectFreeFingerprint(),
      FINGERPRINT_TIMEOUT_MS
    );
    return {
      fingerprintId: result.visitorId,
      fingerprintType: AntiFraudType.FREE,
    };
  } catch (error) {
    console.error("getFingerprint - Unable to get a fingerprint id", error);
    if (isProVersion) {
      console.warn("getFingerprint - Fallback to the free version");
      return getFingerprint(AntiFraudType.FREE);
    }
  }
  return {
    fingerprintId: "",
    fingerprintType: antiFraudType,
  };
};
